<template>
  <div class="scene-selection-modal" v-if="show">
    <div class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5><i class="fas fa-satellite"></i> Select Satellite Scene</h5>
          <button type="button" class="close" @click="closeModal">
            <span>&times;</span>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Sticky filter bar: compact row with date range + sources summary + search -->
          <div class="filter-bar sticky-top-bar">
            <div class="filter-row">
              <div class="filter-cell">
                <label for="startDate" class="filter-label">From</label>
                <input
                  type="date"
                  id="startDate"
                  class="form-control form-control-sm"
                  v-model="dateRange.startDate"
                />
              </div>
              <div class="filter-cell">
                <label for="endDate" class="filter-label">To</label>
                <input
                  type="date"
                  id="endDate"
                  class="form-control form-control-sm"
                  v-model="dateRange.endDate"
                />
              </div>
              <div class="filter-cell filter-cell-grow">
                <label class="filter-label">
                  <span>Sources</span>
                  <button
                    v-if="catalog.length"
                    type="button"
                    class="btn btn-outline-secondary btn-sm source-toggle-btn ml-2"
                    @click="sourcesExpanded = !sourcesExpanded"
                  >
                    <i :class="sourcesExpanded ? 'fas fa-times' : 'fas fa-pen'"></i>
                    {{ sourcesExpanded ? 'Close picker' : 'Edit sources' }}
                  </button>
                </label>
                <div class="source-summary">
                  <span
                    v-for="id in activeCollections.slice(0, 3)"
                    :key="id"
                    class="source-summary-chip"
                  >{{ catalogIcon(id) }} {{ catalogLabel(id) }}</span>
                  <span
                    v-if="activeCollections.length > 3"
                    class="source-summary-more"
                  >+{{ activeCollections.length - 3 }} more</span>
                  <span
                    v-if="activeCollections.length === 0"
                    class="text-danger small"
                  >Pick at least one source</span>
                </div>
              </div>
              <div class="filter-cell filter-cell-search">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="handleSearch"
                  :disabled="loading || activeCollections.length === 0"
                >
                  <i class="fas fa-search"></i> Search
                </button>
              </div>
            </div>

            <!-- Collapsible source picker (scrollable when large) -->
            <div v-if="sourcesExpanded && catalog.length" class="source-selector-expanded">
              <div class="source-selector-controls">
                <button
                  type="button"
                  class="btn btn-primary btn-sm source-done-btn"
                  @click="sourcesExpanded = false"
                >
                  <i class="fas fa-check"></i> Done editing sources
                </button>
                <div class="source-toggle-group" role="group">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm source-toggle-btn"
                    @click="selectAllSources"
                  >
                    <i class="fas fa-check-double"></i> All
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm source-toggle-btn"
                    @click="clearSources"
                  >
                    <i class="fas fa-times"></i> None
                  </button>
                </div>
              </div>
              <small class="text-muted d-block mb-2">
                <i class="fas fa-info-circle"></i>
                Click the <i class="fas fa-info-circle source-info-hint"></i> on any source to learn what it's for.
              </small>
              <div class="source-chips-scroll">
                <div class="source-chips">
                  <div
                    v-for="coll in catalog"
                    :key="coll.id"
                    class="source-chip-wrap"
                  >
                    <label
                      class="source-chip"
                      :class="{ active: activeCollections.includes(coll.id), sar: coll.kind === 'sar' }"
                    >
                      <input type="checkbox" :value="coll.id" v-model="activeCollections" />
                      <span class="source-chip-icon">{{ coll.icon }}</span>
                      <span class="source-chip-main">
                        <span class="source-chip-label">{{ coll.label }}</span>
                        <span class="source-chip-meta">{{ coll.resolution }} · {{ coll.agency }}</span>
                      </span>
                      <button
                        type="button"
                        class="source-info-btn"
                        :class="{ active: sourceInfoId === coll.id }"
                        @click.prevent.stop="toggleSourceInfo(coll.id)"
                        :title="sourceInfoId === coll.id ? 'Hide details' : 'What is this?'"
                      >
                        <i class="fas fa-info-circle"></i>
                      </button>
                    </label>
                    <div v-if="sourceInfoId === coll.id" class="source-info-panel">
                      <p class="mb-1">{{ coll.description }}</p>
                      <small class="text-muted">
                        <strong>Kind:</strong> {{ coll.kind }} ·
                        <strong>Resolution:</strong> {{ coll.resolution }} ·
                        <strong>Provider:</strong> {{ coll.agency }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Scrollable scene results region -->
          <div class="scene-results-region">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading scenes...</span>
            </div>
            <p class="mt-2">Searching for satellite scenes...</p>
          </div>
          
          <!-- No Scenes Found — show only the Load latest banners -->
          <div v-else-if="!scenes || scenes.length === 0" class="py-3">
            <p class="text-muted small mb-2">
              No scenes in the selected date range. Load the latest available scenes from the archive:
            </p>
            <div v-if="emptyBannerItems.length" class="empty-banners">
              <div
                v-for="item in emptyBannerItems"
                :key="item.id"
                class="empty-banner"
              >
                <span class="empty-banner-icon">{{ item.icon }}</span>
                <span class="empty-banner-text">
                  <strong>{{ item.label }}</strong>
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary empty-banner-btn"
                  @click="$emit('loadLatestForCollection', item.id)"
                >
                  <i class="fas fa-clock-rotate-left"></i> Load latest
                </button>
              </div>
            </div>
            <p v-else class="text-muted small mb-0">
              No sources selected. Pick one or more sources above and try again.
            </p>
          </div>
          
          <!-- Scenes List -->
          <div v-else class="scenes-list">
            <div class="mb-3">
              <small class="text-muted">
                Found {{ scenes.length }} scenes • Showing most recent first
              </small>
            </div>

            <!-- Per-collection empty-range banners -->
            <div v-if="emptyBannerItems.length" class="empty-banners mb-3">
              <div
                v-for="item in emptyBannerItems"
                :key="item.id"
                class="empty-banner"
              >
                <span class="empty-banner-icon">{{ item.icon }}</span>
                <span class="empty-banner-text">
                  <strong>{{ item.label }}</strong> — no scenes in this date range.
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary empty-banner-btn"
                  @click="$emit('loadLatestForCollection', item.id)"
                >
                  <i class="fas fa-clock-rotate-left"></i> Load latest
                </button>
              </div>
            </div>
            
            <div class="list-group">
              <div
                v-for="scene in scenes"
                :key="scene.id"
                class="list-group-item list-group-item-action scene-item"
                :class="{ 'selected': selectedScene && selectedScene.id === scene.id }"
                @click="selectScene(scene)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="scene-info">
                    <div class="scene-header">
                      <span class="source-icon">{{ scene.sourceIcon }}</span>
                      <strong>{{ scene.source }}</strong>
                      <span class="scene-id">{{ scene.id.substring(0, 20) }}...</span>
                    </div>
                    
                    <div class="scene-details">
                      <div class="detail-row">
                        <span class="detail-label">{{ sceneDateLabel(scene) }}:</span>
                        <span class="detail-value">{{ sceneDateDisplay(scene) }}</span>
                      </div>

                      <div class="detail-row" v-if="isSar(scene)">
                        <span class="detail-label">Polarizations:</span>
                        <span class="detail-value">
                          {{ (scene.properties['sar:polarizations'] || []).join(' / ') || '—' }}
                        </span>
                      </div>
                      <div class="detail-row" v-else-if="hasCloudCover(scene)">
                        <span class="detail-label">Cloud Cover:</span>
                        <span class="detail-value">
                          <span :class="getCloudCoverClass(scene.properties['eo:cloud_cover'])">
                            {{ formatCloud(scene.properties['eo:cloud_cover']) }}
                          </span>
                        </span>
                      </div>
                      <div class="detail-row" v-else-if="isComposite(scene)">
                        <span class="detail-label">Product:</span>
                        <span class="detail-value">Cloud-filtered composite</span>
                      </div>

                      <div class="detail-row">
                        <span class="detail-label">Resolution:</span>
                        <span class="detail-value">
                          {{ scene.collectionMeta ? scene.collectionMeta.resolution : resolutionFor(scene) }}
                        </span>
                      </div>

                      <div class="detail-row">
                        <span class="detail-label">Agency:</span>
                        <span class="detail-value">{{ scene.agency || '—' }}</span>
                      </div>

                      <div class="detail-row">
                        <span class="detail-label">Assets:</span>
                        <span class="detail-value">
                          {{ getAvailableAssetsCount(scene) }} available
                        </span>
                      </div>
                    </div>
                    <div class="scene-badges mt-1">
                      <span class="badge badge-kind" :class="isSar(scene) ? 'badge-sar' : 'badge-optical'">
                        {{ isSar(scene) ? 'SAR' : 'Optical' }}
                      </span>
                      <span v-if="(scene.collection || '').startsWith('HLS')" class="badge badge-nasa">NASA HLS</span>
                      <span v-if="scene.__loadedLatest" class="badge badge-archive" title="Outside the current date range — loaded from full archive">
                        <i class="fas fa-clock-rotate-left"></i> Archive
                      </span>
                    </div>
                  </div>
                  
                                      <div class="scene-actions" v-if="selectedScene && selectedScene.id === scene.id">
                      <span class="badge badge-success">
                        <i class="fas fa-check"></i> Selected
                      </span>
                    </div>
                </div>
              </div>
            </div>
          </div>
          </div><!-- /.scene-results-region -->
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button 
            v-if="selectedScene"
            type="button" 
            class="btn btn-primary"
            @click="confirmSelection"
          >
            <i class="fas fa-check"></i> Use Selected Scene
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console, max-len */
export default {
  name: 'SceneSelectionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    scenes: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    catalog: {
      // [{ id, label, icon, resolution, agency, kind, description }]
      type: Array,
      default: () => []
    },
    initialCollections: {
      type: Array,
      default: () => []
    },
    emptyCollections: {
      // Collection IDs that returned 0 results for the current date range.
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedScene: null,
      activeCollections: [],
      sourcesExpanded: false,
      sourceInfoId: null, // which source's info panel is expanded, if any
      dateRange: {
        startDate: '',
        endDate: ''
      }
    }
  },
  mounted() {
    this.initializeDateRange()
    this.initializeCollections()
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeDateRange()
        this.initializeCollections()
      }
    },
    initialCollections: {
      immediate: true,
      handler(v) { if (v && v.length) this.activeCollections = [...v] }
    }
  },
  computed: {
    emptyBannerItems() {
      // Only show banners for collections the user actually selected
      const byId = Object.fromEntries(this.catalog.map(c => [c.id, c]))
      return (this.emptyCollections || [])
        .filter(id => this.activeCollections.includes(id))
        .map(id => byId[id])
        .filter(Boolean)
    }
  },
  methods: {
    initializeDateRange() {
      // Default to 90 days ago -> today
      const endDate = new Date()
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
      
      // Format dates for input[type="date"]
      this.dateRange.endDate = endDate.toISOString().split('T')[0]
      this.dateRange.startDate = startDate.toISOString().split('T')[0]
    },
    
    initializeCollections() {
      if (this.activeCollections.length === 0) {
        if (this.initialCollections && this.initialCollections.length) {
          this.activeCollections = [...this.initialCollections]
        } else if (this.catalog && this.catalog.length) {
          // Default = non-NASA-auth collections (public COGs renderable in-browser)
          this.activeCollections = this.catalog
            .filter(c => c.endpoint !== 'nasaCmrStac')
            .map(c => c.id)
        }
      }
    },

    toggleSourceInfo(id) {
      this.sourceInfoId = this.sourceInfoId === id ? null : id
    },

    catalogEntry(id) {
      return (this.catalog || []).find(c => c.id === id)
    },
    catalogLabel(id) {
      const c = this.catalogEntry(id)
      if (!c) return id
      // Compact label: first word or short form
      const shortLabel = c.label.replace(/\s*\(.*?\)\s*/g, '').trim()
      return shortLabel.length > 18 ? shortLabel.slice(0, 16) + '…' : shortLabel
    },
    catalogIcon(id) {
      const c = this.catalogEntry(id)
      return c ? c.icon : '🛰️'
    },

    selectAllSources() {
      this.activeCollections = this.catalog.map(c => c.id)
    },

    clearSources() {
      this.activeCollections = []
    },

    isSar(scene) {
      return (scene.kind || scene.collectionMeta?.kind) === 'sar' ||
        String(scene.collection || '').startsWith('sentinel-1')
    },

    formatCloud(v) {
      if (v === null || v === undefined) return '—'
      return `${Math.round(v * 10) / 10}%`
    },

    resolutionFor(scene) {
      const c = String(scene.collection || '')
      if (c.startsWith('sentinel-2')) return '10 m'
      if (c.startsWith('sentinel-1')) return '10 m'
      if (c.startsWith('landsat')) return '30 m'
      if (c.startsWith('HLS')) return '30 m'
      return '—'
    },

    handleSearch() {
      const start = new Date(this.dateRange.startDate)
      const end = new Date(this.dateRange.endDate)

      if (start > end) {
        alert('From Date cannot be after To Date')
        return
      }

      // Collapse the source picker when the farmer commits a search —
      // frees up vertical space for the results.
      this.sourcesExpanded = false
      this.sourceInfoId = null

      this.$emit('search', {
        startDate: start,
        endDate: end,
        collections: [...this.activeCollections]
      })
    },
    
    closeModal() {
      this.selectedScene = null
      this.$emit('close')
    },
    
    selectScene(scene) {
      this.selectedScene = scene
    },
    
    confirmSelection() {
      if (this.selectedScene) {
        this.$emit('select', this.selectedScene)
        this.closeModal()
      }
    },
    
    saveAsDataset(scene) {
      this.$emit('saveAsDataset', scene)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Unknown'

      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDateShort(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },

    /**
     * Pick the right timestamp for a scene.
     * Most scenes have `datetime`, but STAC composites (MODIS 16-day, Daymet,
     * WorldCover, land-cover products) set `datetime: null` and use
     * `start_datetime` / `end_datetime` for the observation window.
     */
    sceneDateDisplay(scene) {
      const p = scene.properties || {}
      if (p.datetime) return this.formatDate(p.datetime)
      if (p.start_datetime && p.end_datetime) {
        return `${this.formatDateShort(p.start_datetime)} → ${this.formatDateShort(p.end_datetime)}`
      }
      if (p.start_datetime) return this.formatDateShort(p.start_datetime)
      if (p.end_datetime) return this.formatDateShort(p.end_datetime)
      return 'Unknown'
    },

    sceneDateLabel(scene) {
      const p = scene.properties || {}
      if (!p.datetime && (p.start_datetime || p.end_datetime)) return 'Period'
      return 'Date'
    },

    hasCloudCover(scene) {
      const p = scene.properties || {}
      return typeof p['eo:cloud_cover'] === 'number'
    },

    isComposite(scene) {
      const id = String(scene.collection || scene.collectionMeta?.id || '')
      if (/modis|worldcover|io-lulc|daymet|gnatsgo|3dep/i.test(id)) return true
      const p = scene.properties || {}
      return !p.datetime && (p.start_datetime || p.end_datetime)
    },
    
    getCloudCoverClass(cloudCover) {
      const cover = cloudCover || 0
      if (cover <= 10) return 'text-success'
      if (cover <= 30) return 'text-warning'
      return 'text-danger'
    },
    
    getAvailableAssetsCount(scene) {
      if (!scene.assets) return 0
      return Object.keys(scene.assets).length
    }
  }
}
</script>

<style scoped>
.empty-banners {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.empty-banner {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #5d4037;
}
.empty-banner-icon {
  font-size: 1.1rem;
}
.empty-banner-text {
  flex: 1;
}
.empty-banner-btn {
  white-space: nowrap;
}

.scene-selection-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
}

.modal-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 1100px;
  width: 95%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h5 {
  margin: 0;
  color: #333;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.close:hover {
  color: #333;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  min-height: 0;
}

/* Sticky filter bar: compact row with dates, sources summary, search */
.filter-bar.sticky-top-bar {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  border-bottom: 1px solid #e5e9f0;
  padding: 0.75rem 1.25rem;
  flex-shrink: 0;
}
.filter-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
}
.filter-cell { display: flex; flex-direction: column; min-width: 130px; }
.filter-cell-grow { flex: 1; min-width: 220px; }
.filter-cell-search { min-width: 100px; }
.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 3px 0;
  display: flex;
  align-items: center;
}
.source-summary {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
  min-height: 28px;
}
.source-summary-chip {
  font-size: 0.75rem;
  background: #e9f3fe;
  color: #0b5ed7;
  padding: 3px 9px;
  border-radius: 12px;
  white-space: nowrap;
}
.source-summary-more {
  font-size: 0.72rem;
  color: #6c757d;
  padding: 3px 0;
}

.source-selector-expanded {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e5e9f0;
}
.source-selector-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.source-done-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}
.source-info-hint {
  color: #0b5ed7;
  margin: 0 1px;
}
.source-chips-scroll {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fafbfc;
  padding: 10px;
}
.source-chip-wrap { display: block; margin-bottom: 8px; }
.source-chip-wrap:last-child { margin-bottom: 0; }
.source-info-panel {
  margin: 6px 0 4px 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #cfe2ff;
  border-left: 3px solid #0b5ed7;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #212529;
  line-height: 1.5;
}
.source-info-btn {
  background: none;
  border: 1px solid transparent;
  color: #6c757d;
  padding: 4px 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.12s;
  margin-left: auto;
  flex-shrink: 0;
}
.source-info-btn:hover {
  background: #e9f3fe;
  color: #0b5ed7;
  border-color: #cfe2ff;
}
.source-info-btn.active {
  background: #0b5ed7;
  color: #fff;
}

/* Scrollable region that fills remaining modal height */
.scene-results-region {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  min-height: 0;
}

.source-selector {
  background-color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}
.source-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.source-chips { display: flex; flex-direction: column; gap: 6px; }
.source-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  transition: all 0.15s ease;
  margin: 0;
  width: 100%;
}
.source-chip input { display: none; }
.source-chip:hover { border-color: #0B3D91; background: #f8f9fa; }
.source-chip.active {
  background: #eaf2ff;
  border-color: #0B3D91;
  box-shadow: 0 1px 4px rgba(11,61,145,0.15);
}
.source-chip.active.sar {
  background: #fff4ea;
  border-color: #FC3D21;
  box-shadow: 0 1px 4px rgba(252,61,33,0.15);
}
.source-chip-icon { font-size: 1.25em; flex-shrink: 0; }
.source-chip-main { display: flex; flex-direction: column; flex: 1; }
.source-chip-label { font-weight: 600; font-size: 0.92em; }
.source-chip-meta { font-size: 0.74em; color: #666; }

.scene-badges { display: flex; gap: 6px; }
.badge-kind { font-size: 0.72em; padding: 2px 8px; border-radius: 10px; }
.badge-optical { background: #e7f1ff; color: #0B3D91; }
.badge-sar { background: #ffe8e0; color: #b22d17; }
.badge-nasa { background: #0B3D91; color: #fff; font-size: 0.72em; padding: 2px 8px; border-radius: 10px; }
.badge-archive { background: #fff3cd; color: #856404; font-size: 0.72em; padding: 2px 8px; border-radius: 10px; border: 1px solid #ffe082; }

.source-toggle-group { display: inline-flex; gap: 4px; }
.source-toggle-btn {
  font-size: 0.75rem;
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  color: #495057;
  background: #fff;
  border-color: #ced4da;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.source-toggle-btn:hover {
  background: #f1f3f5;
  color: #0b5ed7;
  border-color: #0b5ed7;
}
.source-toggle-btn:focus { box-shadow: 0 0 0 0.15rem rgba(11,94,215,0.25); }

.date-range-filter {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #dee2e6;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.scenes-list {
  max-height: 60vh;
  overflow-y: auto;
}

.scene-item {
  cursor: pointer;
  border: 1px solid #dee2e6;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.scene-item:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.scene-item.selected {
  border-color: #28a745;
  background-color: #d4edda;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2);
}

.scene-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.source-icon {
  font-size: 1.2em;
}

.scene-id {
  font-family: monospace;
  font-size: 0.9em;
  color: #666;
}

.scene-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 0.9em;
}

.detail-row {
  display: flex;
  justify-content: space-between;
}

.detail-label {
  font-weight: 500;
  color: #666;
}

.detail-value {
  color: #333;
}

.scene-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.badge-success {
  background-color: #28a745;
  color: white;
  font-size: 0.8em;
  padding: 0.4em 0.6em;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .scene-details {
    grid-template-columns: 1fr;
  }
  
  .scene-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
